import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import confetti from "canvas-confetti";
import { Trophy, Crown } from "lucide-react";
import { api } from "../lib/api";
import BoardView, { TurnBanner, ClueCard } from "../components/party/BoardView";
import { PlayerAvatar } from "../components/party/PlayerAvatar";
import { BrandMark, ScreenWheel, ScreenBingo, ScreenHunt, ScreenSong, ActivityFeed } from "../components/party/GameVisuals";
import { PathRace, ShotGame, MysteryGame } from "../components/party/MoreGames";
import { goFullscreen } from "../components/party/TvShare";
import { Maximize } from "lucide-react";
import { NewsCorner } from "../components/party/NewsCorner";

const noop = async () => null;
function BigMore({ g }) {
  const C = { shot: ShotGame, mystery: MysteryGame }[g.type] || PathRace;
  return <C g={g} me={null} act={noop} load={() => {}} big />;
}

const MEDAL = ["#C8A96B", "#C0C0C0", "#CD7F32"];

// Board games: whoever collected every clue card takes 1st regardless of points.
function podiumOrder(g) {
  const win = g.state?.winner && (g.players || []).find((p) => p.player_id === g.state.winner);
  if (!win) return g.leaderboard;
  const first = g.leaderboard.find((r) => r.name === win.name) || { ...win, points: g.state.points?.[win.player_id] || 0 };
  return [first, ...g.leaderboard.filter((r) => r !== first)];
}

function fire() {
  const end = Date.now() + 3500;
  (function frame() {
    confetti({ particleCount: 6, angle: 60, spread: 70, origin: { x: 0 }, colors: ["#C8A96B", "#ffffff"] });
    confetti({ particleCount: 6, angle: 120, spread: 70, origin: { x: 1 }, colors: ["#C8A96B", "#ffffff"] });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

function Podium({ top }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = [setTimeout(() => setStep(1), 600), setTimeout(() => setStep(2), 1800), setTimeout(() => { setStep(3); fire(); }, 3200)];
    return () => t.forEach(clearTimeout);
  }, []);
  const order = [1, 0, 2];
  const height = ["h-72", "h-52", "h-40"];
  const showAt = [3, 2, 1];
  return (
    <div className="flex items-end justify-center gap-6 mt-12" data-testid="party-podium">
      {order.map((i) => top[i] && (
        <div key={i} className={`flex flex-col items-center w-56 transition-[opacity,transform] duration-700 ${step >= showAt[i] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} data-testid={`podium-${i + 1}`}>
          {i === 0 && <Crown className="w-12 h-12 text-[#C8A96B] mb-2 animate-bounce" />}
          <PlayerAvatar p={top[i]} size={i === 0 ? 96 : 72} />
          <p className={`font-serif text-center leading-tight mt-3 ${i === 0 ? "text-5xl" : "text-3xl"}`}>{top[i].name}</p>
          <p className="text-white/60 mt-1 mb-4">{top[i].points} pts</p>
          <div className={`w-full ${height[i]} rounded-t-2xl flex items-start justify-center pt-4`} style={{ background: `linear-gradient(to bottom, ${MEDAL[i]}, ${MEDAL[i]}33)` }}>
            <span className="font-serif text-6xl text-black/70">{i + 1}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function PartyScreen() {
  const { code } = useParams();
  const [g, setG] = useState(null);
  const [err, setErr] = useState("");
  const wasOpen = useRef(null);
  const [reveal, setReveal] = useState(false);
  useEffect(() => {
    const load = () => api.get(`/public/party/${code}?screen=1`).then((r) => {
      setG(r.data);
      if (wasOpen.current === true && !r.data.is_open) setReveal(true);
      if (wasOpen.current === null && !r.data.is_open) setReveal(true);
      if (r.data.is_open) setReveal(false);
      wasOpen.current = r.data.is_open;
      setErr("");
    }).catch((e) => { if (e?.response?.status === 404) setErr("Game not found"); });
    load();
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [code]);
  if (err) return <div className="min-h-screen bg-[#0b0b0b] text-white flex items-center justify-center font-serif text-3xl">{err}</div>;
  if (!g) return <div className="min-h-screen bg-[#0b0b0b]" />;
  const join = `${window.location.origin}/party/${g.code}`;
  const Live = { wheel: ScreenWheel, bingo: ScreenBingo, hunt: ScreenHunt, song: ScreenSong, candy: BigMore, wife: BigMore, hubby: BigMore, shot: BigMore, mystery: BigMore }[g.type];
  return (
    <div className="min-h-screen bg-[#0b0b0b] text-white px-10 py-8 overflow-hidden" data-testid="party-screen">
      <button onClick={goFullscreen} className="fixed bottom-4 right-4 z-20 opacity-40 hover:opacity-100 text-xs px-3 py-2 rounded-full bg-white/10 flex items-center gap-1" data-testid="screen-fullscreen-btn"><Maximize className="w-3.5 h-3.5" /> Full screen</button>
      <div className="flex items-start justify-between gap-6">
        <div>
          <BrandMark big className="mb-3" />
          <p className="font-script text-[#C8A96B] text-4xl">{g.couple_names || "Party games"}</p>
          <h1 className="font-serif font-light text-5xl">{g.title}</h1>
        </div>
        {!reveal && <div className="text-center"><div className="bg-white p-3 rounded-2xl"><QRCodeSVG value={join} size={120} /></div><p className="text-xs uppercase tracking-[0.25em] mt-2 text-white/60">Join · {g.code}</p></div>}
      </div>
      {reveal ? (
        <div className="text-center mt-6" data-testid="party-reveal">
          <p className="text-sm uppercase tracking-[0.4em] text-white/60">And the winners are</p>
          {g.leaderboard.length ? <Podium top={podiumOrder(g).slice(0, 3)} /> : <p className="font-serif text-4xl mt-16 text-white/70">No scores this time. Thanks for playing!</p>}
        </div>
      ) : g.type === "board" && g.missing?.length ? (
        <p className="font-serif text-4xl text-white/70 mt-24 text-center" data-testid="party-screen-not-ready">The couple is still setting up this game. Get ready to roll!</p>
      ) : g.type === "board" ? (
        <div className="grid grid-cols-[1fr_340px] gap-8 mt-4 items-start" data-testid="party-screen-board-game">
          <BoardView g={g} big />
          <div className="space-y-5">
            <TurnBanner g={g} />
            <ClueCard g={g} />
            <ScreenBoard rows={g.leaderboard} max={6} />
          </div>
        </div>
      ) : g.phase === "lobby" ? (
        <div className="text-center mt-20 space-y-4" data-testid="party-screen-lobby"><p className="font-serif text-6xl">Scan to join</p><p className="text-2xl text-white/70">The game starts when the host taps Start</p><p className="text-[#C8A96B] text-xl">{(g.players || []).length} players ready</p></div>
      ) : (
        <div className="grid grid-cols-[1fr_360px] gap-10 mt-6 items-start" data-testid="party-screen-board">
          <div>{g.type === "bingo" && g.phase && <div className="text-center mb-6" data-testid="party-screen-bingo-call"><p className="text-sm uppercase tracking-[0.4em] text-white/50">Call {(g.called || []).length}</p><p className="font-serif text-7xl text-[#C8A96B]">{(g.called || []).slice(-1)[0] || "Get ready…"}</p></div>}<Live g={g} /></div>
          <div className="space-y-8"><ScreenBoard rows={g.leaderboard} max={6} /><NewsCorner slug={g.wedding_slug} /><ActivityFeed g={g} /></div>
        </div>
      )}
    </div>
  );
}

function ScreenBoard({ rows, max }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-4 flex items-center gap-2"><Trophy className="w-4 h-4 text-[#C8A96B]" /> Live leaderboard</p>
      {rows.length === 0 && <p className="font-serif text-3xl text-white/60">Scan to join, then start playing!</p>}
      {rows.slice(0, max).map((p, i) => (
        <div key={p.name + i} className="flex items-center gap-4 py-3 border-b border-white/10 text-3xl font-serif">
          <span className="w-8 text-[#C8A96B]">{i + 1}</span><PlayerAvatar p={p} size={40} /><span className="flex-1 truncate">{p.name}</span><span className="text-white/70">{p.points}</span>
        </div>
      ))}
    </div>
  );
}
